<template>
    <div class="py-2" :class="{ 'shaded-light py-4 px-6 mt-3': is_top_level }">
        <div v-if="value.send_from == null">
            <v-alert
                border="left"
                colored-border
                color="primary"
                elevation="0"
                type="error"
                class="mt-2 mb-6"
                :class="{ 'shaded-light': !is_top_level }"
                dense
                dismissible
            >
                <strong>Warning:</strong> No messages will be <span v-if="value.send_mode == 'auto'">sent</span><span v-else>generated</span> because you have not yet selected a send from number
            </v-alert>
        </div>
       
        <m-edit-text
            label="Send Mode"
            :items="send_modes"
            :value="prettyPrintSendMode(value.send_mode)"
            :disabled="disable_mode"
            :custom_on_edit="onEditSendMode"
            :read_only="read_only || disable_mode"
        />

        <m-edit-message-patterns-compact
            :value="value.message_patterns"
            :context="message_pattern_context"
            class="mt-3 mb-6"
            :disabled="read_only"
            @input="onChangeMessagePatterns"
        />

        <div>
            <rc-text-expand-button
                :expanded="advanced_settings_expanded"
                @click="advanced_settings_expanded = !advanced_settings_expanded"
            >
                Advanced Settings
            </rc-text-expand-button>

            <div v-show="advanced_settings_expanded" class="mt-2 px-md-6">
                <v-row>
                    <v-col cols="12" md="6">
                        <m-edit-select-organization-owned-endpoint
                            label="Send From"
                            :organization_id="value.organization_id"
                            v-model="value.send_from"
                            :disabled="read_only"
                            @input="onChangeSendFrom"
                        />
                    </v-col>

                    <v-col cols="12" md="6">
                        <v-select
                            label="Link Shortener Host"
                            :items="link_shortener_host_choices"
                            :value="value.link_shortener_host"
                            :disabled="read_only"
                            @change="onChangeLinkShortener"
                        />
                    </v-col>
                </v-row>

                <v-alert
                    v-if="show_order_conf_link_shortening_info"
                    type="info"
                    border="left"
                    colored-border
                    color="info"
                    elevation="0"
                    class="mt-0 mb-4"
                    dense
                >
                    <div class="text-body-2">
                        <strong>Why is link shortening disabled by default for order confirmation messages?</strong><br>
                        Order confirmation links are already pre-shortened for SMS delivery. Therefore, global link shortening is turned off by default. If you add custom URLs to this message then consider re-enabling link shortening to reduce the size of all links.

                    </div>

                </v-alert>

                <v-row align="end" class="mt-0">
                    <v-col cols="12" md="6">
                        <m-edit-blobs
                            label="Image (optional)"
                            empty_text="No image"
                            v-model="value.blob_ids"
                            :allow_multiple="false"
                            :limit_to_size="460800"
                            style="border-bottom: 1px solid rgb(148,148,148);"
                            class="pb-1"
                            edit_icon="mdi-lead-pencil"
                            dialog_title="Select Image"
                            dialog_hint="<b>Image limitations:</b>  Images <b>must</b> be 450 Kb or smaller.<br/><br/><b>Image size:  </b>For best results, images should be square or close to square.  The ideal image size is 400 x 400 pixels."
                            :read_only="read_only"
                            process_media
                            externally_visible
                            @input="onChangeBlobs"
                        />
                    </v-col>

                    <v-col cols="12" md="6">
                        <m-select-vcard
                            v-model="value.vcard_id"
                            label="Attach contact card (optional)"
                            ref="select-contact-card"
                            :read_only="read_only"
                            hide_details
                            @input="onChangeVCard"
                        />
                    </v-col>
                </v-row>

                <v-row v-if="default_message_control">
                    <v-col cols="12" md="6">
                        <m-edit-text
                            label="Delay before sending"
                            :value="value.time_delay_send_after_enqueue"
                            duration
                            :read_only="read_only"
                            @input="onChangeDelayBeforeSendingDefault"
                        />
                    </v-col>

                    <v-col cols="12" md="6">
                        <m-edit-text
                            label="Expire if not sent within"
                            :value="value.time_delay_expire_after_trigger"
                            duration
                            :read_only="read_only"
                            @input="onChangeExpirationDefault"
                        />
                    </v-col>
                </v-row>

                <v-row v-if="!default_message_control">
                    <v-col cols="12" md="6">
                        <m-edit-text
                            label="Custom delay before sending"
                            :value="value.custom_time_delay_send_after_enqueue"
                            duration
                            unset_label="Remove"
                            :read_only="read_only"
                            @input="onChangeDelayBeforeSendingCustom"
                        />
                    </v-col>

                    <v-col cols="12" md="6">
                        <m-edit-text
                            label="Custom expire if not sent within"
                            :value="value.custom_time_delay_expire_after_trigger"
                            duration
                            unset_label="Remove"
                            :read_only="read_only"
                            @input="onChangeExpirationCustom"
                        />
                    </v-col>
                </v-row>
            </div>
        </div>

        <v-alert
            v-if="value.time_delay_send_after_enqueue >= value.time_delay_expire_after_trigger"  
            color="primary"
            elevation="2"
            type="error"
        >
            With the "expire if not sent within" time set to less than or equal to the "delay before sending" <b><u>every message will expire before it is sent.</u></b>
        </v-alert>

        <m-auto-message-set-send-mode-dialog
            ref="set-send-mode-dialog"
            :template_context="message_pattern_context"
            :template_subject_id="template_subject_id"
            @change="onChangeSendMode"
        />
    </div>
</template>

<script>
const _ = require('lodash');

export default {
    props: {
        message_pattern_context: {
            type: String,
            default: function() { return ''; }
        },

        template_subject_id: {
            type: String,
            default: function() { return null; }
        },

        send_from_choices: {
            type: Array,
            default: function() { return []; }
        },

        value: {
            type: Object,
            default: function() { return []; }
        },

        link_shortener_host_choices: {
            type: Array,
            default: function() { return []; }
        },

        disable_mode: {
            type: Boolean,
            default: function() { return false; }
        },

        default_message_control: {
            type: Boolean,
            default: function() { return false; }
        },

        read_only: {
            type: Boolean,
            default: function() { return false; }
        },

        /**
         * Indicates whether this is being used as a top-level message control section (as opposed to inside conditional messages)
         * Styling is different for visual flow in that case.
         */
        is_top_level: {
            type: Boolean,
            default: function() { return false; }
        }
    },

    data() {
        return {
             send_modes: [
                { 'text' : 'Disabled', 'value' : 'disabled'},
                { 'text' : 'Manual', 'value' : 'manual'},
                { 'text' : 'Automatic', 'value' : 'auto'},
            ],
            advanced_settings_expanded: false,
        }
    },

    computed: {
        /**
         * Shows informational alert explaining why link shortening is disabled for order confirmation messages.
         * Only displays when:
         * 1. The template context is 'order-placed-request-order-confirmation'
         * 2. Link shortening is set to "Do not shorten links"
         */
        show_order_conf_link_shortening_info() {
            const is_order_confirmation = this.message_pattern_context === 'order-placed-request-order-confirmation';
            const is_link_shortening_disabled = this.value.link_shortener_host === 'do-no-shorten-link-under-any-circumstances.biz';
            return is_order_confirmation && is_link_shortening_disabled;
        },
    },


    methods: {
        onChangeMessagePatterns(new_value) 
        {
            let emit_value = _.cloneDeep(this.value);
            emit_value.message_patterns = new_value;
            this.$emit('input', emit_value);
        },

        onChangeBlobs(new_value) 
        {
            let emit_value = _.cloneDeep(this.value);
            emit_value.blob_ids = new_value;
            this.$emit('input', emit_value);
        },

        onChangeVCard(new_value) 
        {
            let emit_value = _.cloneDeep(this.value);
            emit_value.vcard_id = new_value;
            this.$emit('input', emit_value);
        },

        onChangeSendMode(new_value) 
        {
            let emit_value = _.cloneDeep(this.value);
            emit_value.send_mode = new_value;
            this.$emit('input', emit_value);
        },

        onChangeSendFrom(new_value) 
        {
            let emit_value = _.cloneDeep(this.value);
            emit_value.send_from = new_value;
            this.$emit('input', emit_value);
        },

        onChangeLinkShortener(new_value)
        {
            let emit_value = _.cloneDeep(this.value);
            emit_value.link_shortener_host = new_value;
            this.$emit('input', emit_value);
        },

        onChangeDelayBeforeSendingDefault(new_value)
        {
            let emit_value = _.cloneDeep(this.value);
            emit_value.time_delay_send_after_enqueue = new_value;
            this.$emit('input', emit_value);
        },

        onChangeExpirationDefault(new_value)
        {
            if ( new_value == 0 )
            {
                this.$rc.alert('Invalid expire if not sent within', 'You may not set the expiration to zero -- all messages will immediately expire<br/><br/>No changes have been made.');
                return;
            }

            let emit_value = _.cloneDeep(this.value);
            emit_value.time_delay_expire_after_trigger = new_value;
            this.$emit('input', emit_value);
        },

        onChangeDelayBeforeSendingCustom(new_value)
        {
            let emit_value = _.cloneDeep(this.value);
            emit_value.custom_time_delay_send_after_enqueue = new_value;
            this.$emit('input', emit_value);
        },

        onChangeExpirationCustom(new_value)
        {
            let emit_value = _.cloneDeep(this.value);
            emit_value.custom_time_delay_expire_after_trigger = new_value;
            this.$emit('input', emit_value);
        },

        onEditSendMode()
        {
            this.$refs['set-send-mode-dialog'].showDialog(this.value.send_mode);
        },

        prettyPrintSendMode(mode)
        {
            let ret = this.send_modes.find(item => item.value === mode);

            if ( ret == null ) return mode;
            return ret.text;
        }
    },

}
</script>

<style lang="scss" scoped>

</style>
